/* eslint-disable no-unused-vars */
/**
 * layer-list-panels.js
 * One exported builder function per complex layer panel.
 * Each function receives exactly the callbacks it needs and returns
 * an object suitable for eventItem.panel = { content, ... }
 * OR directly mutates eventItem where the original did so.
 *
 * No Vue context, no ArcGIS modules — only DOM + the ui-helpers.
 */

import {
    createSummaryButton,
    createResetButton,
    createStyledButton,
    createFilterSection,
    createButtonRow,
    createLabeledDateInput,
    createLabeledTextInput,
    createCheckboxGroup,
    createRadioGroup,
    createToggleSwitch,
    createColorSwatch,
    createRasterLegendItem,
    createPolygonLegendItem,
  } from "./layer-list-ui-helpers";
  
  import {
    populateAutocompleteOptions,
    createAutocomplete,
  } from "../search-inputs/autocomplete-utility";
  
  // ─── Simple summary-button-only panels ───────────────────────────────────────
  
  export function buildSimpleSummaryPanel(onSummaryClick) {
    const summaryBtn = createSummaryButton("Show Layer Summary", onSummaryClick);
    return {
      content: ["legend", "&nbsp;", summaryBtn],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  }
  
  // ─── Releaf Assets ────────────────────────────────────────────────────────────
  
  export function buildReleafAssetsPanel({
    releafFactoryTypeFilter,
    releafFactoryTypeFilterReset,
    releafFactoryStatusFilter,
    handleReleafFactorySummary,
  }) {
    const summaryBtn = createSummaryButton("Show Layer Summary", handleReleafFactorySummary);
  
    // Factory Type — checkboxes
    const typeLabels = [
      "Refinery",
      "Crushing Mill",
      "Deshelling Mill",
      "Kraken II",
      "Carbonizer",
      "Biochar Facility",
    ];
    const typeGroup = createCheckboxGroup(
      typeLabels,
      "checkboxGroupFactoryType",
      (selected) => releafFactoryTypeFilter(selected)
    );
  
    // Factory Status — radios
    const statusLabels = [
      "Active",
      "Inactive",
      "Underconstruction",
      "Planned (2025)",
      "Planned (2026)",
    ];
    const statusGroup = createRadioGroup(
      statusLabels,
      "radioGroupFactoryStatus",
      (index) => releafFactoryStatusFilter(index)
    );
  
    const resetBtn = createResetButton(() => {
      document.querySelectorAll(".radioButton").forEach((rb) => {
        rb.checked = false;
      });
      releafFactoryTypeFilterReset();
    });
  
    return {
      content: [
        "legend",
        "Factory Type Filter",
        "&nbsp;",
        typeGroup,
        "&nbsp;",
        "Factory Status Filter",
        "&nbsp;",
        statusGroup,
        "&nbsp;",
        resetBtn,
        "&nbsp;",
        summaryBtn,
      ],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  }
  
  // ─── Customer Factories ───────────────────────────────────────────────────────
  
  export function buildCustomerFactoriesPanel({
    customerFactoryTypeFilter,
    customerFactoryTypeFilterReset,
    customerFactoryStatusFilter,
    handleCustomerFactorySummary,
  }) {
    const summaryBtn = createSummaryButton("Show Layer Summary", handleCustomerFactorySummary);
  
    const typeLabels = ["Refinery", "Crushing Mill", "Deshelling Mill", "Kraken II"];
    const typeGroup = createCheckboxGroup(
      typeLabels,
      "checkboxGroupFactoryType",
      (selected) => customerFactoryTypeFilter(selected)
    );
  
    const statusLabels = ["Active", "Inactive", "Underconstruction"];
    const statusGroup = createRadioGroup(
      statusLabels,
      "radioGroupFactoryStatus",
      (index) => customerFactoryStatusFilter(index)
    );
  
    const resetBtn = createResetButton(() => {
      document.querySelectorAll(".radioButton").forEach((rb) => {
        rb.checked = false;
      });
      customerFactoryTypeFilterReset();
    });
  
    return {
      content: [
        "legend",
        "Factory Type Filter",
        "&nbsp;",
        typeGroup,
        "&nbsp;",
        "Factory Status Filter",
        "&nbsp;",
        statusGroup,
        "&nbsp;",
        resetBtn,
        "&nbsp;",
        summaryBtn,
      ],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  }
  
  // ─── Processing Factories ─────────────────────────────────────────────────────
  
  export function buildProcessingFactoriesPanel({ processedFactoryStatusFilter }) {
    const biomassLabels = [
      "Field Crops & Grains",
      "Tree Crops & Fruits",
      "Palm Kernel",
      "Industrial Processing Waste",
      "Food Waste",
      "Animal-based",
      "Wood",
    ];
  
    const biomassGroup = createCheckboxGroup(
      biomassLabels,
      "checkboxGroupBiomassType",
      () => updateFilters(),
      true
    );
  
    // Source group — manual build because "Others" maps to multiple values
    const sourceContainer = document.createElement("div");
    sourceContainer.style.marginTop = "10px";
    sourceContainer.style.marginBottom = "10px";
  
    const sourceItems = [
      { label: "Biochar Partners", value: "Biochar Partners", dataValues: null, color: "rgba(34, 139, 34, 0.8)" },
      { label: "Others", value: "Others", dataValues: ["GRID", "Google"], color: "rgba(50, 205, 50, 0.8)" },
    ];
  
    sourceItems.forEach((item, index) => {
      const wrapper = document.createElement("div");
      wrapper.className = "radio-option";
      wrapper.style.display = "flex";
      wrapper.style.alignItems = "center";
  
      const cb = document.createElement("input");
      cb.className = "radioButton";
      cb.type = "checkbox";
      cb.name = "checkboxGroupSource";
      cb.value = item.value;
      cb.checked = true;
      if (item.dataValues) cb.setAttribute("data-values", JSON.stringify(item.dataValues));
      cb.addEventListener("change", () => updateFilters());
  
      // Triangle colour indicator
      const indicator = document.createElement("div");
      indicator.style.width = "0";
      indicator.style.height = "0";
      indicator.style.borderLeft = "6px solid transparent";
      indicator.style.borderRight = "6px solid transparent";
      indicator.style.borderBottom = `12px solid ${item.color}`;
      indicator.style.marginLeft = "8px";
      indicator.style.marginRight = "8px";
  
      const lbl = document.createElement("span");
      lbl.textContent = item.label;
      lbl.className = "radio-label";
  
      wrapper.appendChild(cb);
      wrapper.appendChild(indicator);
      wrapper.appendChild(lbl);
      sourceContainer.appendChild(wrapper);
    });
  
    function updateFilters() {
      const selectedBiomass = [...document.getElementsByName("checkboxGroupBiomassType")]
        .filter((c) => c.checked)
        .map((c) => c.value);
  
      const selectedSources = [...document.getElementsByName("checkboxGroupSource")]
        .filter((c) => c.checked)
        .flatMap((c) => {
          const dv = c.getAttribute("data-values");
          return dv ? JSON.parse(dv) : [c.value];
        });
  
      processedFactoryStatusFilter(selectedBiomass, selectedSources);
    }
  
    const resetBtn = createResetButton(() => {
      document.querySelectorAll(".radioButton").forEach((cb) => (cb.checked = true));
      processedFactoryStatusFilter(biomassLabels, ["Biochar Partners", "GRID", "Google"]);
    });
  
    return {
      content: [
        "<strong>Filter By Biomass Type</strong>",
        " ",
        biomassGroup,
        " ",
        "<strong>Filter By Source</strong>",
        " ",
        sourceContainer,
        " ",
        resetBtn,
        " ",
      ],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  }
  
  // ─── Biochar Partners ─────────────────────────────────────────────────────────
  
  export function buildBiocharPartnersPanel({ biocharPartnersFilter }) {
    const biomassLabels = [
      "Field Crops & Grains",
      "Tree Crops & Fruits",
      "Palm Kernel",
      "Industrial Processing Waste",
      "Food Waste",
      "Animal-based",
      "Wood",
    ];
  
    const biomassGroup = createCheckboxGroup(
      biomassLabels,
      "checkboxGroupBiocharBiomassType",
      () => {
        const selected = [...document.getElementsByName("checkboxGroupBiocharBiomassType")]
          .filter((c) => c.checked)
          .map((c) => c.value);
        biocharPartnersFilter(selected);
      },
      true
    );
  
    const resetBtn = createResetButton(() => {
      document.querySelectorAll(".radioButton").forEach((cb) => (cb.checked = true));
      biocharPartnersFilter(biomassLabels);
    });
  
    return {
      content: [
        "<strong>Filter By Biomass Type</strong>",
        " ",
        biomassGroup,
        " ",
        resetBtn,
        " ",
      ],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  }
  
  // ─── Maize Farms ──────────────────────────────────────────────────────────────
  
  export function buildMaizeFarmsPanel({ processedFarmStatusFilter }) {
    const farmLabels = ["Both", "Commercial", "Subsistence"];
  
    const farmGroup = createCheckboxGroup(
      farmLabels,
      "checkboxGroupFarmStatus",
      (selected) => processedFarmStatusFilter(selected),
      true
    );
  
    const resetBtn = createResetButton(() => {
      document.querySelectorAll(".radioButton").forEach((cb) => (cb.checked = true));
      processedFarmStatusFilter(farmLabels);
    });
  
    return {
      content: [
        "legend",
        "Maize Farms Status Filter",
        " ",
        farmGroup,
        " ",
        resetBtn,
        " ",
      ],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  }
  
  // ─── Niger Delta Oil Palm sliders ─────────────────────────────────────────────
  
  export function buildOilPalmSliderPanel(
    Slider,
    { gtWeeklyP1Yield, ltWeeklyP1Yield, gtOilPalmCluster, ltOilPalmCluster, oilPalmClusterReset, handleOilpalmClusterSummary }
  ) {
    const weeklyP1YieldSlider = new Slider({
      min: 0, max: 300, values: [0], precision: 0,
      visibleElements: { labels: true, rangeLabels: true },
    });
  
    const oilPalmClusterSlider = new Slider({
      min: 0, max: 20, values: [0], precision: 0,
      visibleElements: { labels: true, rangeLabels: true },
    });
  
    const switch1 = createToggleSwitch(gtWeeklyP1Yield, ltWeeklyP1Yield);
    const switch2 = createToggleSwitch(gtOilPalmCluster, ltOilPalmCluster);
  
    const resetBtn = createResetButton(() => {
      oilPalmClusterReset();
      weeklyP1YieldSlider.values = [0];
      oilPalmClusterSlider.values = [0];
    });
  
    const summaryBtn = createSummaryButton("Show Layer Summary", handleOilpalmClusterSummary);
  
    const panel = {
      content: [
        "legend",
        "Toggle off the switch to reverse the slider function to '< less than function'.",
        "&nbsp;",
        "Weekly P1 Yield (T)",
        "&nbsp;",
        switch1,
        "&nbsp;",
        weeklyP1YieldSlider,
        "&nbsp;",
        "&nbsp;",
        "Palm Cluster by Age",
        "&nbsp;",
        switch2,
        "&nbsp;",
        oilPalmClusterSlider,
        "&nbsp;",
        resetBtn,
        "&nbsp;",
        summaryBtn,
      ],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  
    return { panel, weeklyP1YieldSlider, oilPalmClusterSlider };
  }
  
  // ─── P1 LGA Summary slider ────────────────────────────────────────────────────
  
  export function buildLGASummarySliderPanel(Slider, { gtP1LGASummary, ltP1LGASummary }) {
    const p1LGASummarySlider = new Slider({
      min: 0, max: 73000, values: [0], precision: 0,
      visibleElements: { labels: true, rangeLabels: true },
    });
  
    const toggle = createToggleSwitch(gtP1LGASummary, ltP1LGASummary);
  
    const panel = {
      content: [
        "legend",
        "Toggle off the switch to reverse the slider function to '< less than function'.",
        "&nbsp;",
        "Weekly P1 Yield (T)",
        "&nbsp;",
        toggle,
        "&nbsp;",
        p1LGASummarySlider,
      ],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  
    return { panel, p1LGASummarySlider };
  }
  
  // ─── Roof Footprint slider ────────────────────────────────────────────────────
  
  export function buildRoofFootprintSliderPanel(Slider, { gtBuidingFootprint, ltBuidingFootprint }) {
    const buidingFootprintSlider = new Slider({
      min: 0, max: 10000, values: [0], precision: 0,
      visibleElements: { labels: true, rangeLabels: true },
    });
  
    const toggle = createToggleSwitch(gtBuidingFootprint, ltBuidingFootprint);
  
    const panel = {
      content: [
        "legend",
        "Toggle off the switch to reverse the slider function to '< less than function'.",
        "&nbsp;",
        "Building Footprints (m²)",
        "&nbsp;",
        "&nbsp;",
        toggle,
        "&nbsp;",
        buidingFootprintSlider,
        "&nbsp;",
      ],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  
    return { panel, buidingFootprintSlider };
  }
  
  // ─── Maize Production custom legend ──────────────────────────────────────────
  
  export function buildMaizeProductionPanel(layer, year) {
    const ranges = [
      { minValue: 0,      maxValue: 15000,  label: "0-15,000 MT" },
      { minValue: 15001,  maxValue: 30000,  label: "15,001-30,000 MT" },
      { minValue: 30001,  maxValue: 45000,  label: "30,001-45,000 MT" },
      { minValue: 45001,  maxValue: 60000,  label: "45,001-60,000 MT" },
      { minValue: 60001,  maxValue: 75000,  label: "60,001-75,500 MT" },
      { minValue: 75001,  maxValue: 90000,  label: "75,001-90,000 MT" },
      { minValue: 90001,  maxValue: 105000, label: "90,001-105,000 MT" },
      { minValue: 105001, maxValue: 120000, label: "105,001-120,000 MT" },
    ];
    const colors = [
      "#f7fcf5","#e2f4dd","#bfe6b9","#94d390",
      "#60ba6c","#329b51","#0d7835","#00441b",
    ];
  
    // Give each range a mutable visible flag
    ranges.forEach((r) => (r.visible = true));
  
    function updateLayerFilter() {
      const visibleRanges = ranges.filter((r) => r.visible);
      layer.definitionExpression =
        visibleRanges.length > 0 && visibleRanges.length < ranges.length
          ? visibleRanges
              .map((r) => `(Production >= ${r.minValue} AND Production <= ${r.maxValue})`)
              .join(" OR ")
          : "";
    }
  
    const panel = document.createElement("div");
    panel.className = "custom-legend-panel";
  
    const title = document.createElement("h4");
    title.textContent = `Maize Production ${year}`;
    title.className = "custom-legend-title";
    panel.appendChild(title);
  
    ranges.forEach((range, index) => {
      const { item, checkbox } = createPolygonLegendItem(
        colors[index],
        range.label,
        (checked) => {
          range.visible = checked;
          updateLayerFilter();
        }
      );
      panel.appendChild(item);
    });
  
    return panel;
  }
  
  // ─── Raw Material Source panel ────────────────────────────────────────────────
  
  export function buildRawMaterialSourcePanel(rmSourceLayer, { handleRMSummary }) {
    const panelContainer = document.createElement("div");
    panelContainer.style.padding = "10px";
    panelContainer.style.background = "white";
  
    // -- Legend --
    const legendSection = createFilterSection("Total Tonnage");
    legendSection.style.border = "none";
    legendSection.style.marginBottom = "15px";
  
    const breaks = [
      { min: 1500, max: Infinity, label: "> 1500" },
      { min: 1000, max: 1500,    label: "1000 - 1500" },
      { min: 500,  max: 1000,    label: "500 - 1000" },
      { min: 100,  max: 500,     label: "100 - 500" },
      { min: 10,   max: 100,     label: "10 - 100" },
      { min: 0,    max: 10,      label: "<10" },
    ];
    const getSizeForRange = (min, max) => {
      if (min === 0 && max === 10)  return 8;
      if (min === 1500)             return 24;
      if (max <= 100)  return 12;
      if (max <= 500)  return 16;
      if (max <= 1000) return 20;
      return 22;
    };
    const maxCircleSize = 24 * 2;
  
    breaks.forEach((b) => {
      const item = document.createElement("div");
      item.className = "legend-item";
      item.style.display = "flex";
      item.style.alignItems = "center";
      item.style.marginBottom = "10px";
  
      const size = getSizeForRange(b.min, b.max);
      const circleContainer = document.createElement("div");
      circleContainer.style.width = `${maxCircleSize}px`;
      circleContainer.style.display = "flex";
      circleContainer.style.justifyContent = "center";
      circleContainer.style.alignItems = "center";
      circleContainer.style.marginRight = "10px";
  
      const circle = document.createElement("div");
      circle.style.width = `${size * 2}px`;
      circle.style.height = `${size * 2}px`;
      circle.style.backgroundColor = "#452B1F";
      circle.style.borderRadius = "50%";
      circleContainer.appendChild(circle);
      item.appendChild(circleContainer);
  
      const lbl = document.createElement("span");
      lbl.textContent = `${b.label} tons`;
      item.appendChild(lbl);
      legendSection.appendChild(item);
    });
    panelContainer.appendChild(legendSection);
  
    // -- Filters --
    const filtersSection = createFilterSection("Filters");
    filtersSection.style.border = "none";
  
    // Date range
    const dateSection = createFilterSection("Filter by Date Range");
    let startDateInput, endDateInput;
    const { wrapper: startWrapper, input: sInput } = createLabeledDateInput("Start Date:", (val) => {
      if (rmSourceLayer?.viewModel) rmSourceLayer.viewModel.startDate = val;
    });
    startDateInput = sInput;
    const { wrapper: endWrapper, input: eInput } = createLabeledDateInput("End Date:", (val) => {
      if (rmSourceLayer?.viewModel) rmSourceLayer.viewModel.endDate = val;
    });
    endDateInput = eInput;
    dateSection.appendChild(startWrapper);
    dateSection.appendChild(endWrapper);
    filtersSection.appendChild(dateSection);
  
    // Supplier
    const supplierSection = createFilterSection("Filter by Supplier");
    let supplierInput, supplierAutocomplete;
    const { wrapper: supplierWrapper, input: supInput } = createLabeledTextInput(
      "Supplier Name:",
      "Enter supplier name",
      (val) => { if (rmSourceLayer?.viewModel) rmSourceLayer.viewModel.supplierName = val; }
    );
    supplierInput = supInput;
    supplierSection.appendChild(supplierWrapper);
    filtersSection.appendChild(supplierSection);
  
    // Community
    const communitySection = createFilterSection("Filter by Community");
    let communityInput, communityAutocomplete;
    const { wrapper: communityWrapper, input: comInput } = createLabeledTextInput(
      "Community:",
      "Enter community name",
      (val) => { if (rmSourceLayer?.viewModel) rmSourceLayer.viewModel.community = val; }
    );
    communityInput = comInput;
    communitySection.appendChild(communityWrapper);
    filtersSection.appendChild(communitySection);
  
    panelContainer.appendChild(filtersSection);
  
    // Buttons
    const applyBtn = createStyledButton("Apply Filters", "#4caf50", () => {
      if (rmSourceLayer?.viewModel) rmSourceLayer.viewModel.applyFilters();
    });
    const clearBtn = createStyledButton("Clear Filters", "#f44336", () => {
      if (rmSourceLayer?.viewModel) {
        rmSourceLayer.viewModel.clearDateFilter();
        startDateInput.value = "";
        endDateInput.value = "";
        supplierInput.value = "";
        communityInput.value = "";
        if (supplierAutocomplete) supplierAutocomplete.hide();
        if (communityAutocomplete) communityAutocomplete.hide();
      }
    });
    panelContainer.appendChild(createButtonRow(applyBtn, clearBtn));
  
    const summaryBtn = createSummaryButton("Show Summary", handleRMSummary);
    panelContainer.appendChild(summaryBtn);
  
    // Async: populate autocomplete after DOM is ready
    const populateOptions = async () => {
      try {
        let supplierOptions = await populateAutocompleteOptions(rmSourceLayer, "supplierName");
        if (!supplierOptions.length) supplierOptions = ["Favour Bassey"];
  
        let communityOptions = await populateAutocompleteOptions(rmSourceLayer, "community");
        if (!communityOptions.length) communityOptions = ["Akamkpa", "Biase"];
  
        supplierAutocomplete = createAutocomplete(supplierInput, supplierOptions, (val) => {
          if (rmSourceLayer?.viewModel) rmSourceLayer.viewModel.supplierName = val;
        });
        communityAutocomplete = createAutocomplete(communityInput, communityOptions, (val) => {
          if (rmSourceLayer?.viewModel) rmSourceLayer.viewModel.community = val;
        });
      } catch (err) {
        console.error("Error populating RM autocomplete:", err);
      }
    };
    populateOptions();
  
    return {
      content: [panelContainer],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
      open: false,
    };
  }
  
  // ─── Biochar Applications panel ───────────────────────────────────────────────
  
  export function buildBiocharApplicationsPanel(
    self,
    { biocharTypeFilter, handleShowBiocharLayerSummary, toast }
  ) {
    const panelContainer = document.createElement("div");
    panelContainer.style.padding = "10px";
    panelContainer.style.background = "white";
  
    let activeOption = null;
    let selectedLga = null;
    let selectedCommunity = null;
    let lgaAutocomplete, communityAutocomplete;
  
    // -- Biochar Type Filter --
    const biocharTypeSection = createFilterSection("Filter by Batch Id Category");
    biocharTypeSection.style.borderRadius = "0";
  
    const checkboxGroup = document.createElement("div");
    checkboxGroup.style.display = "flex";
    checkboxGroup.style.flexDirection = "column";
    checkboxGroup.style.gap = "8px";
  
    const biocharTypes = [
      { value: "normal",       label: "Normal",       color: "#8FC54D" },
      { value: "experimental", label: "Experimental", color: "#FFD700" },
    ];
    biocharTypes.forEach((type) => {
      const wrapper = document.createElement("div");
      wrapper.style.display = "flex";
      wrapper.style.alignItems = "center";
      wrapper.style.padding = "4px";
  
      const indicator = document.createElement("div");
      indicator.style.width = "16px";
      indicator.style.height = "16px";
      indicator.style.backgroundColor = type.color;
      indicator.style.marginRight = "10px";
      indicator.style.borderRadius = "2px";
      indicator.style.flexShrink = "0";
  
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.id = `biochar-type-${type.value}`;
      cb.name = "biocharType";
      cb.value = type.value;
      cb.checked = true;
      cb.style.marginRight = "8px";
      cb.style.cursor = "pointer";
      cb.addEventListener("change", () => {
        const selected = [...checkboxGroup.querySelectorAll('input[name="biocharType"]:checked')]
          .map((c) => c.value);
        biocharTypeFilter(selected);
      });
  
      const lbl = document.createElement("label");
      lbl.htmlFor = `biochar-type-${type.value}`;
      lbl.textContent = type.label;
      lbl.style.cursor = "pointer";
      lbl.style.flexGrow = "1";
      lbl.style.marginBottom = "0";
  
      wrapper.appendChild(indicator);
      wrapper.appendChild(cb);
      wrapper.appendChild(lbl);
      checkboxGroup.appendChild(wrapper);
    });
    biocharTypeSection.appendChild(checkboxGroup);
    panelContainer.appendChild(biocharTypeSection);
  
    // -- Date Filter --
    const dateSection = createFilterSection("Filter by Date Range");
  
    const dateOptions = [
      "Today","Yesterday","This Week","Last Week",
      "This Month","Last Month","This Year","Last Year","Custom",
    ];
    const btnGrid = document.createElement("div");
    btnGrid.style.display = "grid";
    btnGrid.style.gridTemplateColumns = "repeat(3, 1fr)";
    btnGrid.style.gap = "5px";
    btnGrid.style.marginBottom = "10px";
  
    const dateButtons = [];
    dateOptions.forEach((option) => {
      const btn = document.createElement("button");
      btn.textContent = option;
      btn.style.padding = "5px";
      btn.style.border = "1px solid #ccc";
      btn.style.borderRadius = "4px";
      btn.style.cursor = "pointer";
      btn.style.backgroundColor = "#f0f0f0";
      btn.style.fontSize = "12px";
      btn.addEventListener("click", () => {
        activeOption = option;
        dateButtons.forEach((b) => {
          b.style.backgroundColor = b.textContent === option ? "#4caf50" : "#f0f0f0";
          b.style.color = b.textContent === option ? "white" : "black";
        });
        customRangeContainer.style.display = option === "Custom" ? "block" : "none";
        if (option !== "Custom") {
          self.applyBiocharPredefinedDateFilter(option.toLowerCase().replace(/\s/g, ""));
        }
      });
      dateButtons.push(btn);
      btnGrid.appendChild(btn);
    });
    dateSection.appendChild(btnGrid);
  
    // Custom date inputs
    const customRangeContainer = document.createElement("div");
    customRangeContainer.style.display = "none";
    customRangeContainer.style.marginTop = "10px";
    let startDateInput, endDateInput;
    const { wrapper: startW, input: sInput } = createLabeledDateInput("Start Date:", (val) => {
      self.biocharStartDate = val;
    });
    startDateInput = sInput;
    const { wrapper: endW, input: eInput } = createLabeledDateInput("End Date:", (val) => {
      self.biocharEndDate = val;
    });
    endDateInput = eInput;
    customRangeContainer.appendChild(startW);
    customRangeContainer.appendChild(endW);
    dateSection.appendChild(customRangeContainer);
    panelContainer.appendChild(dateSection);
  
    // -- LGA Filter --
    const lgaSection = createFilterSection("Filter by Lga");
    const { wrapper: lgaW, input: lgaInput } = createLabeledTextInput(
      "Lga:", "Enter lga name",
      (val) => {
        selectedLga = val;
        if (val) self.applyBiocharLgaFilter(val);
        else self.clearBiocharFilter();
      }
    );
    lgaSection.appendChild(lgaW);
    panelContainer.appendChild(lgaSection);
  
    // -- Community Filter --
    const communitySection = createFilterSection("Filter by Community");
    const { wrapper: comW, input: communityInput } = createLabeledTextInput(
      "Community:", "Enter community name",
      (val) => {
        selectedCommunity = val;
        if (val) self.applyBiocharCommunityFilter(val);
        else self.clearBiocharFilter();
      }
    );
    communitySection.appendChild(comW);
    panelContainer.appendChild(communitySection);
  
    // -- Action Buttons --
    const applyBtn = createStyledButton("Apply Filter", "#4caf50", () => {
      if (activeOption === "Custom") {
        if (!self.biocharStartDate || !self.biocharEndDate) {
          toast.e("Please select both start and end dates.");
          return;
        }
        if (new Date(self.biocharStartDate) > new Date(self.biocharEndDate)) {
          toast.e("Start date must be before end date.");
          return;
        }
        self.applyBiocharDateFilter(selectedLga);
      } else if (activeOption) {
        self.applyBiocharPredefinedDateFilter(
          activeOption.toLowerCase().replace(/\s/g, ""),
          selectedLga
        );
        toast.s(`Biochar applications for ${activeOption} filter applied`);
      } else if (selectedLga) {
        self.applyBiocharLgaFilter(selectedLga);
        toast.s(`Biochar applications for ${selectedLga} filter applied`);
      } else if (selectedCommunity) {
        self.applyBiocharCommunityFilter(selectedCommunity);
        toast.s(`Biochar applications for ${selectedCommunity} filter applied`);
      } else {
        toast.e("Please select a date range option or enter a lga.");
      }
    });
  
    const clearBtn = createStyledButton("Clear Filter", "#f44336", () => {
      self.clearBiocharFilter();
      startDateInput.value = "";
      endDateInput.value = "";
      lgaInput.value = "";
      communityInput.value = "";
      selectedLga = null;
      selectedCommunity = null;
      activeOption = null;
      dateButtons.forEach((b) => {
        b.style.backgroundColor = "#f0f0f0";
        b.style.color = "black";
      });
      customRangeContainer.style.display = "none";
      if (lgaAutocomplete) lgaAutocomplete.hide();
      if (communityAutocomplete) communityAutocomplete.hide();
      checkboxGroup.querySelectorAll('input[name="biocharType"]').forEach((cb) => {
        cb.checked = true;
      });
      biocharTypeFilter(["normal", "experimental"]);
    });
  
    panelContainer.appendChild(createButtonRow(applyBtn, clearBtn));
  
    // -- Summary Button --
    const summarySection = document.createElement("div");
    summarySection.style.marginTop = "20px";
    summarySection.style.paddingTop = "15px";
    const summaryBtn = createSummaryButton("Show Summary", handleShowBiocharLayerSummary);
    summarySection.appendChild(summaryBtn);
    panelContainer.appendChild(summarySection);
  
    // Async: load autocomplete options
    const loadOptions = async () => {
      const biocharLayer = self.map.findLayerById("biocharApplications");
  
      let lgaOptions = await populateAutocompleteOptions(biocharLayer, "lga");
      if (!lgaOptions.length) lgaOptions = ["Yakurr", "Biase", "Markafi", "Zaria"];
      lgaAutocomplete = createAutocomplete(lgaInput, lgaOptions, (val) => { selectedLga = val; });
  
      let commOptions = await populateAutocompleteOptions(biocharLayer, "community");
      if (!commOptions.length) commOptions = ["Ugep", "Ehom", "Idomi"];
      communityAutocomplete = createAutocomplete(communityInput, commOptions, (val) => {
        selectedCommunity = val;
      });
    };
    loadOptions();
  
    return {
      content: [panelContainer],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
      open: false,
    };
  }
  
  // ─── Soil Analysis (feature layer toggle panel) ───────────────────────────────
  
  export function buildSoilAnalysisPanel(eventItem) {
    const toggleContainer = document.createElement("div");
    toggleContainer.className = "toggle-container";
  
    const soilProperties = [
      {
        name: "Soil pH",
        field: "SoilPHDescription",
        ranges: [
          { label: "Acidic (<5.5) - Biochar improves nutrient availability",  value: "Acidic (<5.5) - Biochar improves nutrient availability",  color: "#FF6347" },
          { label: "Neutral (5.5-7.4) - Optimal for maize",                   value: "Neutral (5.5-7.4) - Optimal for maize",                   color: "#FFD700" },
          { label: "Alkaline (>7.4) - Biochar impact varies",                  value: "Alkaline (>7.4) - Biochar impact varies",                  color: "#4682B4" },
        ],
      },
      {
        name: "Soil Carbon",
        field: "SoilCarbonDescription",
        ranges: [
          { label: "Low SOC (< 12 g/kg) - Biochar improves sustainability",    value: "Low SOC (< 12 g/kg) - Biochar improves sustainability",    color: "#D3D3D3" },
          { label: "Moderate SOC (≥ 12 g/kg)) - Biochar still beneficial",    value: "Moderate SOC (≥ 12 g/kg)) - Biochar still beneficial",    color: "#8A2BE2" },
        ],
      },
      {
        name: "Sand Texture",
        field: "SandDescription",
        ranges: [
          { label: "Low sand (<60%) - Biochar impact on moisture retention is lower", value: "Low sand (<60%) - Biochar impact on moisture retention is lower", color: "#DC143C" },
          { label: "Moderate sand (60-70%) - Biochar enhances water-holding",         value: "Moderate sand (60-70%) - Biochar enhances water-holding",         color: "#F7DC6F" },
          { label: "High sand (>70%) - Biochar significantly improves moisture",      value: "High sand (>70%) - Biochar significantly improves moisture",      color: "#32CD32" },
        ],
      },
    ];
  
    let activeProperty = null;
    const selectedFilters = { "Soil pH": [], "Soil Carbon": [], "Sand Texture": [] };
  
    const updateUI = () => {
      toggleContainer.querySelectorAll(".toggle-wrapper").forEach((wrapper) => {
        const radio = wrapper.querySelector(".toggle-radio");
        const cbGroup = wrapper.querySelector(".checkbox-group");
        if (radio.checked) {
          radio.classList.add("active");
          cbGroup.style.display = "block";
        } else {
          radio.classList.remove("active");
          cbGroup.style.display = "none";
        }
      });
    };
  
    const applySoilFilter = () => {
      const activeProp = soilProperties.find((p) => p.name === activeProperty);
      if (eventItem.layer.visible && activeProp && selectedFilters[activeProperty].length > 0) {
        eventItem.layer.renderer = {
          type: "unique-value",
          field: activeProp.field,
          uniqueValueInfos: activeProp.ranges.map((r) => ({
            value: r.value,
            symbol: { type: "simple-marker", color: r.color, size: 12, opacity: 0.8, outline: { color: "gray", width: 0.5 } },
          })),
        };
        eventItem.layer.definitionExpression = selectedFilters[activeProperty]
          .map((v) => `${activeProp.field} = '${v}'`)
          .join(" OR ");
      } else {
        eventItem.layer.definitionExpression = "";
        // No action needed here, redundant assignment removed.
      }
    };
  
    eventItem.watch("visible", (newValue) => {
      if (newValue && !activeProperty) {
        activeProperty = "Sand Texture";
        selectedFilters["Sand Texture"] = soilProperties
          .find((p) => p.name === "Sand Texture")
          .ranges.map((r) => r.value);
        const sandIdx = soilProperties.findIndex((p) => p.name === "Sand Texture");
        const sandRadio = toggleContainer.querySelector(`#toggle-${sandIdx}`);
        if (sandRadio) sandRadio.checked = true;
        toggleContainer.querySelectorAll('input[name="Sand Texture-checkbox"]').forEach((cb) => {
          cb.checked = true;
        });
        updateUI();
        applySoilFilter();
      }
    });
  
    soilProperties.forEach((property, index) => {
      const toggleWrapper = document.createElement("div");
      toggleWrapper.className = "toggle-wrapper";
  
      const inner = document.createElement("div");
      inner.className = "toggle-inner";
  
      const radio = document.createElement("input");
      radio.type = "radio";
      radio.className = "toggle-radio";
      radio.name = "soilPropertyToggle";
      radio.id = `toggle-${index}`;
      radio.checked = false;
      radio.addEventListener("change", () => {
        if (radio.checked) {
          activeProperty = property.name;
          selectedFilters[activeProperty] = property.ranges.map((r) => r.value);
          toggleWrapper.querySelectorAll(".checkboxButton").forEach((cb) => (cb.checked = true));
        } else {
          activeProperty = null;
        }
        updateUI();
        applySoilFilter();
      });
      inner.appendChild(radio);
  
      const lbl = document.createElement("label");
      lbl.htmlFor = `toggle-${index}`;
      lbl.innerText = property.name;
      lbl.className = "toggle-label";
      inner.appendChild(lbl);
      toggleWrapper.appendChild(inner);
  
      const cbGroup = document.createElement("div");
      cbGroup.className = "checkbox-group";
      cbGroup.style.display = "none";
  
      property.ranges.forEach((range, ri) => {
        const optWrapper = document.createElement("div");
        optWrapper.className = "checkbox-option";
  
        const cb = document.createElement("input");
        cb.type = "checkbox";
        cb.className = "checkboxButton";
        cb.name = `${property.name}-checkbox`;
        cb.value = range.value;
        cb.checked = false;
        cb.addEventListener("change", () => {
          if (cb.checked) {
            selectedFilters[property.name].push(range.value);
          } else {
            selectedFilters[property.name] = selectedFilters[property.name].filter(
              (v) => v !== range.value
            );
          }
          applySoilFilter();
        });
  
        const swatch = createColorSwatch(range.color);
        swatch.style.width = "15px";
        swatch.style.height = "15px";
        swatch.style.clipPath = "none";
        swatch.style.borderRadius = "50%";
  
        const rangeLbl = document.createElement("label");
        rangeLbl.htmlFor = `${property.name}-${ri}`;
        rangeLbl.innerText = range.label;
        rangeLbl.className = "checkbox-label";
  
        optWrapper.appendChild(swatch);
        optWrapper.appendChild(rangeLbl);
        optWrapper.appendChild(cb);
        cbGroup.appendChild(optWrapper);
      });
  
      toggleWrapper.appendChild(cbGroup);
      toggleContainer.appendChild(toggleWrapper);
    });
  
    const resetBtn = createResetButton(() => {
      activeProperty = null;
      soilProperties.forEach((p) => {
        selectedFilters[p.name] = [];
        document.getElementsByName(`${p.name}-checkbox`).forEach((cb) => (cb.checked = false));
      });
      document.getElementsByName("soilPropertyToggle").forEach((r) => (r.checked = false));
      updateUI();
      applySoilFilter();
    });
  
    updateUI();
    applySoilFilter();
  
    return {
      content: ["legend", "Soil Property Filters", " ", toggleContainer, " ", resetBtn, " "],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  }
  
  // ─── Raster sublayer legend panels (Soil Raster, Maize Yield, Maize Suitability) ─
  
  export function buildSoilRasterPanel(eventItem, RasterStretchRenderer) {
    const soilLayers = [
      {
        title: "Soil PH",
        ranges: [
          { label: "Acidic (<5.5)",     min: 0,   max: 5.5, color: [255, 99,  71,  255] },
          { label: "Neutral (5.5-7.4)", min: 5.5, max: 7.4, color: [153, 152, 61,  255] },
          { label: "Alkaline (>7.4)",   min: 7.4, max: 14,  color: [50,  205, 50,  255] },
        ],
        defaultRenderer: new RasterStretchRenderer({ stretchType: "min-max", colorRamp: { type: "algorithmic", fromColor: [255, 99, 71, 255], toColor: [50, 205, 50, 255] }, statistics: [{ min: 0, max: 14 }] }),
      },
      {
        title: "Soil PH Africa",
        ranges: [
          { label: "Acidic (<5.5)",     min: 0,   max: 5.5, color: [255, 99,  71,  255] },
          { label: "Neutral (5.5-7.4)", min: 5.5, max: 7.4, color: [153, 152, 61,  255] },
          { label: "Alkaline (>7.4)",   min: 7.4, max: 14,  color: [50,  205, 50,  255] },
        ],
        defaultRenderer: new RasterStretchRenderer({ stretchType: "min-max", colorRamp: { type: "algorithmic", fromColor: [255, 99, 71, 255], toColor: [50, 205, 50, 255] }, statistics: [{ min: 0, max: 14 }] }),
      },
      {
        title: "Soil Carbon",
        ranges: [
          { label: "Low SOC (0-50)",   min: 0,  max: 50,  color: [211, 211, 211, 255] },
          { label: "High SOC (50-100)", min: 50, max: 100, color: [138, 43,  226, 255] },
        ],
        defaultRenderer: new RasterStretchRenderer({ stretchType: "min-max", colorRamp: { type: "algorithmic", fromColor: [211, 211, 211, 255], toColor: [138, 43, 226, 255] }, statistics: [{ min: 0, max: 100 }] }),
      },
      {
        title: "Sand Texture",
        ranges: [
          { label: "Low Sand (<60%)",          min: 0,  max: 60,  color: [220, 20,  60,  255] },
          { label: "Moderate Sand (60-70%)",   min: 60, max: 70,  color: [135, 112, 65,  255] },
          { label: "High Sand (>70%)",         min: 70, max: 100, color: [50,  205, 50,  255] },
        ],
        defaultRenderer: new RasterStretchRenderer({ stretchType: "min-max", colorRamp: { type: "algorithmic", fromColor: [220, 20, 60, 255], toColor: [50, 205, 50, 255] }, statistics: [{ min: 0, max: 100 }] }),
      },
    ];
  
    eventItem.layer.layers.forEach((layer, index) => {
      const config = layer.title
        ? soilLayers.find((c) => c.title === layer.title)
        : soilLayers[index];
      if (!config) { console.warn(`No config for sublayer at index ${index}`); return; }
  
      const sublayerItem = eventItem.children.find((c) => c.layer === layer);
      if (!sublayerItem) { console.warn(`Sublayer item not found for: ${config.title}`); return; }
  
      sublayerItem.title = config.title;
      sublayerItem.visible = false;
  
      const cbContainer = document.createElement("div");
      cbContainer.className = "checkbox-container";
      config.ranges.forEach((range) => cbContainer.appendChild(createRasterLegendItem(range)));
  
      sublayerItem.panel = {
        content: ["legend", `${config.title} Legend`, " ", cbContainer, " ", " "],
        className: "esri-icon-sliders-horizontal",
        title: "Layer Properties",
      };
    });
  }
  
  export function buildMaizeYieldRasterPanel(eventItem) {
    const ranges = [
      { label: "Maize Yield (1-2.99 t/ha)", color: [139, 105, 20,  255] },
      { label: "Maize Yield (3-5 t/ha)",    color: [184, 134, 11,  255] },
      { label: "Maize Yield (5-8 t/ha)",    color: [218, 165, 32,  255] },
      { label: "Maize Yield (8-12 t/ha)",   color: [255, 215, 0,   255] },
    ];
  
    eventItem.layer.layers.forEach((layer, index) => {
      if (index !== 0) return;
      const sublayerItem = eventItem.children.find((c) => c.layer === layer);
      if (!sublayerItem) return;
  
      const cbContainer = document.createElement("div");
      cbContainer.className = "checkbox-container";
      ranges.forEach((r) => cbContainer.appendChild(createRasterLegendItem(r)));
  
      sublayerItem.panel = {
        content: ["legend", "Maize Yield Legend", " ", cbContainer, " ", " "],
        className: "esri-icon-sliders-horizontal",
        title: "Layer Properties",
      };
    });
  }
  
  export function buildMaizeSuitabilityRasterPanel(eventItem) {
    const ranges = [
      { label: "Very Low",            color: [255, 0,   0,   255] },
      { label: "Low to Moderate",     color: [170, 43,  0,   255] },
      { label: "Moderate to High",    color: [85,  85,  0,   255] },
      { label: "High",                color: [0,   128, 0,   255] },
    ];
  
    eventItem.layer.layers.forEach((layer, index) => {
      if (index !== 0) return;
      const sublayerItem = eventItem.children.find((c) => c.layer === layer);
      if (!sublayerItem) return;
  
      const cbContainer = document.createElement("div");
      cbContainer.className = "checkbox-container";
      ranges.forEach((r) => cbContainer.appendChild(createRasterLegendItem(r)));
  
      sublayerItem.panel = {
        content: ["legend", "Maize Suitability Legend", " ", cbContainer, " ", " "],
        className: "esri-icon-sliders-horizontal",
        title: "Layer Properties",
      };
    });
  }
  
  export function buildMaizeAreaPanel() {
    const ranges = [{ label: "Maize Areas", color: [255, 255, 0, 255] }];
    const cbContainer = document.createElement("div");
    cbContainer.className = "checkbox-container";
    ranges.forEach((r) => cbContainer.appendChild(createRasterLegendItem(r)));
  
    return {
      content: ["Maize Area Legend", " ", cbContainer],
      className: "esri-icon-sliders-horizontal",
      title: "Layer Properties",
    };
  }